import React from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  Flex,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Text,
  Link,
  Box,
} from '@chakra-ui/react';

import { PaymentMethodsList } from './components';
import { IPaymentMethodNew } from './types';
import { getWalletDataQuery } from '../../../gql/queries/getUserPaymentMethods';
import { EGTMEvents, useGTMDataLayer } from '../../../hooks';
import DollarWithCircleIcon from '../../../Icons/DollarWithCircleIcon';
import { FanCash } from '../../../Icons/FanCash';
import { GiftIcon } from '../../../Icons/GiftIcon';
import { getLink } from '../../../Navigation/utils';
import { EButtonVariant, EColor, ETextVariant } from '../../../Theme';
import { useModalContext } from '../../hooks/useModalContext';
import { useModalStore } from '../../stores';
import { EModalContent } from '../../types';
import { dollars, toCurrency } from '../../utils';

export const MyWallet = () => {
  const modalStore = useModalStore();
  const pushGTMData = useGTMDataLayer();
  const { fanaticsCollectBaseUrl } = useModalContext();

  const { data: userWalletData, loading } = useQuery(getWalletDataQuery, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
    variables: {
      enableFancash: true,
      enableRewards: true,
    },
  });

  const paymentMethodsData = userWalletData?.collectCurrentUserV2
    ?.collectPaymentMethods as IPaymentMethodNew[];

  const { usableFunds } =
    userWalletData?.collectCurrentUserV2?.accountFunds ?? {};
  const usableAmount = toCurrency(dollars(usableFunds?.amountInCents ?? 0));
  const fancashBalance = toCurrency(
    dollars(userWalletData?.fancashBalance?.balance?.amount ?? 0)
  );
  const rewardsBalance = toCurrency(
    dollars(userWalletData?.currentBalance?.amount ?? 0)
  );

  const isMaxMethodsCount = (paymentMethodsData?.length ?? 0) >= 10;

  const handleAddPaymentClick = () => {
    pushGTMData({
      event: EGTMEvents.PROFILE_WALLET_ADD_PAYMENT_BUTTON_CLICKED,
    });
    modalStore.navigate({ name: EModalContent.CHOOSE_PAYMENT_METHOD });
  };

  return (
    <>
      <ModalHeader
        display="flex"
        flexDir="column"
        justifyContent="center"
        minHeight="70px"
        pb="12px"
        position="relative"
        pt={{ base: 0, md: '8px' }}
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          position="relative"
          w="100%"
        >
          <Text
            color={EColor.Black}
            fontSize="18px"
            fontWeight="700"
            lineHeight="24px"
            textAlign="center"
          >
            Wallet
          </Text>
        </Flex>
        <Text
          color={EColor.Black}
          fontSize="16px"
          fontWeight="700"
          lineHeight="20px"
          mt="16px"
        >
          Balances
        </Text>
        <Flex flexDir="column" gap="0">
          {/* Account Funds */}
          <Flex
            alignItems="center"
            bg={EColor.White}
            borderRadius="8px"
            gap="12px"
            p="8px"
            px="0"
          >
            <Flex
              alignItems="center"
              bg={EColor.Neutral10}
              borderRadius="6px"
              h="32px"
              justifyContent="center"
              shrink="0"
              w="48px"
            >
              <DollarWithCircleIcon />
            </Flex>
            <Flex flex="1" flexDir="column" gap="2px" minW="0">
              <Flex alignItems="center" justifyContent="space-between">
                <Text
                  color={EColor.Black}
                  fontSize="14px"
                  fontWeight="700"
                  lineHeight="18px"
                >
                  Account Funds
                </Text>
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <Text
                    color={EColor.Black}
                    fontSize="14px"
                    fontWeight="700"
                    lineHeight="18px"
                  >
                    {usableAmount}
                  </Text>
                )}
              </Flex>

              <Flex gap="8px">
                <Link
                  color={EColor.Black}
                  fontSize="12px"
                  fontWeight="400"
                  href={getLink(fanaticsCollectBaseUrl, 'payoutsCreate')}
                  lineHeight="16px"
                  textDecoration="underline"
                >
                  Request payout
                </Link>
                <Link
                  color={EColor.Black}
                  fontSize="12px"
                  fontWeight="400"
                  href={getLink(fanaticsCollectBaseUrl, 'payouts')}
                  lineHeight="16px"
                  textDecoration="underline"
                >
                  Payout history
                </Link>
              </Flex>
            </Flex>
          </Flex>

          {/* FanCash */}
          <Flex
            alignItems="center"
            bg={EColor.White}
            borderRadius="8px"
            gap="12px"
            p="8px"
            px="0"
          >
            <Flex
              alignItems="center"
              bg={EColor.Neutral10}
              borderRadius="6px"
              h="32px"
              justifyContent="center"
              shrink="0"
              w="48px"
            >
              <Box boxSize="20px">
                <FanCash />
              </Box>
            </Flex>
            <Text
              color={EColor.Black}
              flex="1"
              fontSize="14px"
              fontWeight="700"
              lineHeight="18px"
            >
              FanCash
            </Text>
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <Text
                color={EColor.Black}
                fontSize="14px"
                fontWeight="700"
                lineHeight="18px"
              >
                {fancashBalance}
              </Text>
            )}
          </Flex>

          {/* Rewards */}
          <Flex
            alignItems="center"
            bg={EColor.White}
            borderRadius="8px"
            gap="12px"
            p="8px"
            px="0"
          >
            <Flex
              alignItems="center"
              bg={EColor.Neutral10}
              borderRadius="6px"
              h="32px"
              justifyContent="center"
              shrink="0"
              w="48px"
            >
              <GiftIcon boxSize="20px" color={EColor.Black} />
            </Flex>
            <Text
              color={EColor.Black}
              flex="1"
              fontSize="14px"
              fontWeight="700"
              lineHeight="18px"
            >
              Rewards
            </Text>
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <Text
                color={EColor.Black}
                fontSize="14px"
                fontWeight="700"
                lineHeight="18px"
              >
                {rewardsBalance}
              </Text>
            )}
          </Flex>
        </Flex>
      </ModalHeader>

      <ModalBody
        maxH={{ base: '100%', md: '287px' }}
        minHeight={{ base: '100%', md: '287px' }}
        overflowX="hidden"
        overflowY="auto"
        p="0 24px 24px"
        sx={{
          '&::-webkit-scrollbar': {
            width: '16px',
            borderRadius: '100px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: EColor.Neutral15,
            border: '6px solid rgba(0, 0, 0, 0)',
            borderRadius: '100px',
            backgroundClip: 'padding-box',
          },
        }}
      >
        <PaymentMethodsList
          isLoading={loading}
          paymentMethodsData={paymentMethodsData}
        />
      </ModalBody>

      <ModalFooter flexDir="column" px="24px" py="16px">
        {isMaxMethodsCount && (
          <Text
            color={EColor.Black}
            fontSize="10px"
            letterSpacing="0.1px"
            pb="12px"
            variant={ETextVariant.Paragraph5}
          >
            You&apos;ve reached your maximum amount of payment methods. Please
            delete one to add another.
          </Text>
        )}

        <Button
          borderRadius="full"
          fontSize="16px"
          fontWeight="700"
          h="56px"
          isDisabled={loading || isMaxMethodsCount}
          textTransform="none"
          variant={EButtonVariant.BRAND_PRIMARY}
          w="100%"
          onClick={handleAddPaymentClick}
        >
          Add payment method
        </Button>
      </ModalFooter>
    </>
  );
};
